import React from "react";
import { MdEdit, MdDelete } from "react-icons/md";

const DepenseTable = ({ depenses, loading, onEdit, onDelete }) => {
  // Fonction pour formater le montant en devise (MGA)
  const formatCurrency = (amount) => {
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount)) return "0 Ar";
    
    return new Intl.NumberFormat('fr-MG', {
      style: 'currency',
      currency: 'MGA',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(numAmount);
  };

  // Formatage de la date
  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      }).format(date);
    } catch (error) {
      return dateString;
    }
  };

  return (
    <div className="overflow-hidden rounded-lg shadow-sm border border-gray-200">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th
              scope="col"
              className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-700"
            >
              Date
            </th>
            <th
              scope="col"
              className="px-3 py-3.5 text-left text-sm font-semibold text-gray-700"
            >
              Désignation
            </th>
            <th
              scope="col"
              className="px-3 py-3.5 text-left text-sm font-semibold text-gray-700"
            >
              Quantité
            </th>
            <th
              scope="col"
              className="px-3 py-3.5 text-left text-sm font-semibold text-gray-700"
            >
              Prix unitaire
            </th>
            <th
              scope="col"
              className="px-3 py-3.5 text-left text-sm font-semibold text-gray-700"
            >
              Montant total
            </th>
            <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 bg-white">
          {loading && depenses.length === 0 ? (
            <tr>
              <td colSpan="6" className="px-3 py-6 text-center text-sm text-gray-500">
                <div className="flex justify-center">
                  <svg className="animate-spin h-5 w-5 text-[#57A863]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </div>
              </td>
            </tr>
          ) : depenses.length === 0 ? (
            <tr>
              <td colSpan="6" className="px-3 py-6 text-center text-sm text-gray-500">
                <p>Aucune dépense trouvée</p>
                <p className="text-xs text-gray-400 mt-1">
                  Créez une nouvelle dépense en cliquant sur le bouton "Nouvelle Dépense"
                </p>
              </td>
            </tr>
          ) : (
            depenses.map((depense) => {
              const totalAmount = parseFloat(depense.pu_depense) * parseFloat(depense.qte_depense);
              
              return (
                <tr key={depense.id_caisse} className="hover:bg-gray-50 transition-colors">
                  <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                    {formatDate(depense.date_depense)}
                  </td>
                  <td className="px-3 py-4 text-sm text-gray-600">
                    <div className="flex flex-col">
                      <span className="font-medium text-gray-900">{depense.designation}</span>
                      {depense.desc_depense && (
                        <span className="mt-1 text-xs text-gray-500 max-w-xs truncate" title={depense.desc_depense}>
                          {depense.desc_depense}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-600">
                    {depense.qte_depense}
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-600">
                    {formatCurrency(depense.pu_depense)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm font-semibold text-gray-900">
                    {formatCurrency(totalAmount)}
                  </td>
                  <td className="whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => onEdit(depense)}
                        className="p-1.5 text-[#57A863] hover:text-[#4c9657] rounded-md hover:bg-gray-100 transition-colors"
                        title="Modifier"
                      >
                        <MdEdit className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => onDelete(depense)}
                        className="p-1.5 text-red-500 hover:text-red-700 rounded-md hover:bg-gray-100 transition-colors"
                        title="Supprimer"
                      >
                        <MdDelete className="h-5 w-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })
          )}
          
          {/* Ligne pour afficher le total */}
          {depenses.length > 0 && (
            <tr className="bg-gray-50 font-bold">
              <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm text-gray-700" colSpan="4">
                Total
              </td>
              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900 font-bold">
                {formatCurrency(
                  depenses.reduce(
                    (sum, depense) => sum + parseFloat(depense.pu_depense) * parseFloat(depense.qte_depense),
                    0
                  )
                )}
              </td>
              <td></td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default DepenseTable;